import React from 'react';
import {
  Input,
  Textarea,
  Select,
  Button,
  PaperClip,
  DatePicker,
  Table,
  Tooltip,
  AreaSelector,
} from 'components';
import { ProfileService, AuthService } from 'services';
import moment from 'moment';
import { connect } from 'react-redux';
import { RootState } from 'reducers';
import { Globals, Constants } from 'utils';
import { RouteComponentProps } from 'react-router-dom';
import {
  PhoneIcon,
  WebIcon,
  InstagramIcon,
  FacebookIcon,
  LocationIcon,
  TimeIcon,
  AddIcon,
  TrashIcon,
  PasswordActive,
  PasswordInactive,
} from 'assets/icons';
import { WhiteImage } from 'assets/img';
import { ContainerCardMembership } from './client/memberships/memberships';
import PHONE_CODES from 'utils/phonecodes';
import { isValidPhoneNumber } from 'libphonenumber-js';
import COUNTRIES from 'utils/countries';

interface Props extends RootState, RouteComponentProps {
  dispatch: any;
}

class Profile extends React.Component<Props> {
  state = {
    form: {
      name: '',
      lastname: '',
      email: '',
      phone: '',
      phonecode_id: '',
      phone_code: PHONE_CODES[0].value,
      document_type_id: '',
      document: '',
      password: '',
      password_confirmation: '',
      city_id: '',
      country: '',
      state_id: '',
      gender_id: '',
      biography: '',
      practice_since: null,
      transfer_bank_id: '',
      transfer_account: '',
      transfer_type: '',
      transfer_document_type_id: '',
      transfer_document: '',
      payment_mobile_bank_id: '',
      payment_mobile_document_type_id: '',
      payment_mobile_document: '',
      payment_mobile_phone: '',
      payment_mobile_phonecode_id: '',
    },
    activePassword: false,
    activePasswordConfirmation: false,
    user: null,
    states: [],
    banks: [],
    cities: [],
    document_types: [],
    phonecodes: [],
    genders: [],
    plan_user: null,
    areas: [],
    user_areas: [],
  };

  componentDidMount() {
    this.props.dispatch({
      type: 'SET_HEADER',
      payload: {
        title: 'Mi Cuenta',
        onBack: null,
      },
    });

    this.props.dispatch({
      type: 'SET_SUBHEADER',
      payload: {
        title: '',
      },
    });

    this.load();
  }

  load = async () => {
    const res: any = await ProfileService.get({
      user_id: this.props.user.id,
    });
    const practiceSince = res.user?.practice_since;
    const isValidDate = moment(practiceSince, 'YYYY-MM-DD', true).isValid();
    this.setState(
      {
        user: res.user,
        banks: res.banks || [],
        states: res.states || [],
        document_types: res.document_types || [],
        phonecodes: res.phonecodes || [],
        genders: res.genders || [],
        plan_user: res.plan_user,
        areas: res.areas || [],
        user_areas: (res.user_areas || []).map((i: any) => i.area_id),
        form: {
          ...this.state.form,
          name: res.user?.name || '',
          lastname: res.user?.lastname || '',
          email: res.user?.email || '',
          city_id: res.user?.city_id || '',
          state_id: res.user?.state_id || '',
          country: res.user?.country || '',
          transfer_bank_id: res.bank_data?.transfer_bank_id || '',
          transfer_account: res.bank_data?.transfer_account || '',
          transfer_type: res.bank_data?.transfer_type || '',
          transfer_document_type_id:
            res.bank_data?.transfer_document_type_id || res.document_types.length > 0
              ? res.document_types[0].id
              : '',
          transfer_document: res.bank_data?.transfer_document || '',
          payment_mobile_bank_id: res.bank_data?.payment_mobile_bank_id || '',
          payment_mobile_document_type_id:
            res.bank_data?.payment_mobile_type_id || res.document_types.length > 0
              ? res.document_types[0].id
              : '',
          payment_mobile_document: res.bank_data?.payment_mobile_document || '',
          payment_mobile_phone: res.bank_data?.payment_mobile_phone || '',
          payment_mobile_phonecode_id:
            res.bank_data?.payment_mobile_phonecode_id || res.phonecodes.length > 0
              ? res.phonecodes[0].id
              : '',
          /**
           * Normalización de phone_code para compatibilidad con el selector.
           */
          phone_code: (() => {
            const raw = res.user?.phone_code || PHONE_CODES[0].value;
            const matched = PHONE_CODES.find((p: any) => p.value === raw || p.callingCode === raw);
            return matched?.value || PHONE_CODES[0].value;
          })(),

          /**
           * Reconstrucción del número de teléfono completo con prefijo de operadora.
           */
          phone: (() => {
            let fullPhone = res.user?.phone || '';
            if (res.user?.phonecode_id && res.phonecodes?.length > 0) {
              const operadora = res.phonecodes.find((p: any) => p.id === res.user.phonecode_id);
              if (operadora) {
                // Extraer el prefijo de operadora sin el "0" inicial
                // Ejemplo: "0414" → "414"
                const prefix = operadora.name.replace(/^0/, '');
                // Solo agregar el prefijo si el número no lo tiene ya,
                // para evitar duplicación en caso de que ya esté completo
                if (!fullPhone.startsWith(prefix)) {
                  fullPhone = prefix + fullPhone;
                }
              }
            }
            return fullPhone;
          })(),

          phonecode_id: res.user?.phonecode_id || '',
          document: res.user?.document || '',
          document_type_id: res.user?.document_type_id || '',
          practice_since: isValidDate ? moment(practiceSince, 'YYYY-MM-DD').toDate() : null,
          gender_id: res.user?.gender_id || '',
          biography: res.user?.biography || '',
        },
      },
      () => {
        if (res.user?.city_id) {
          this.getCities();
        }
      },
    );
  };

  submit = async (e: any) => {
    e.preventDefault();
    const { phone, phone_code } = this.state.form;
    const { form } = this.state;
    const isVenezuela = form.country === 'VE';

    if (form.password !== form.password_confirmation) {
      Globals.showError('Las contraseñas no coinciden.');
      return;
    }

    if (!!form.password) {
      const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).+$/;
      if (!passwordRegex.test(form.password)) {
        Globals.showError(
          'La contraseña debe tener al menos una mayúscula, una minúscula, un número y un símbolo',
        );
        return;
      }

      if (form.password.length < 6) {
        Globals.showError('La contraseña debe tener al menos 6 caracteres');
        return;
      }
    }

    const isClientOrLawyer =
      this.props.user?.level_id == Constants.LEVELS.CLIENT ||
      this.props.user?.level_id == Constants.LEVELS.LAWYER;

    // Validación de campos obligatorios de ubicación y documento (solo aplica a clientes y abogados)
    if (isClientOrLawyer) {
      // Validación solo para el teléfono principal (solo aplica a clientes y abogados)
      if (phone && !isValidPhoneNumber(phone, phone_code as any)) {
        Globals.showError('El número de teléfono personal es inválido.');
        return;
      }
      if (!form.country) {
        Globals.showError('Por favor, seleccione su país.');
        return;
      }
      if (isVenezuela && !form.state_id) {
        Globals.showError('Por favor, seleccione su estado.');
        return;
      }
      if (isVenezuela && !form.city_id) {
        Globals.showError('Por favor, seleccione su ciudad.');
        return;
      }
      if (!form.document_type_id) {
        Globals.showError('Por favor, seleccione el tipo de documento.');
        return;
      }
      if (!form.document) {
        Globals.showError('Por favor, ingrese su número de documento.');
        return;
      }
    }

    // Validación de campos obligatorios comunes
    // Solo letras (incluyendo acentos y ñ) y espacios
    const nameRegex = /^[a-zA-ZáéíóúÁÉÍÓÚñÑ\s]+$/;

    if (!form.name) {
      Globals.showError('Por favor, ingrese su nombre.');
      return;
    }

    if (!nameRegex.test(form.name.trim())) {
      Globals.showError('El nombre no debe contener números ni signos de puntuación');
      return;
    }

    if (!form.lastname) {
      Globals.showError('Por favor, ingrese su apellido.');
      return;
    }

    if (!nameRegex.test(form.lastname.trim())) {
      Globals.showError('El apellido no debe contener números ni signos de puntuación');
      return;
    }

    if (!form.email) {
      Globals.showError('Por favor, ingrese su correo electrónico.');
      return;
    }

    if (form.email && !/\S+@\S+\.\S+/.test(form.email)) {
      Globals.showError('Por favor, ingrese un correo electrónico válido.');
      return;
    }

    // Limpiar campos que no aplican al admin/moderador
    const extraFields = isClientOrLawyer
      ? {}
      : {
          country: '',
          phone: '',
          phone_code: '',
          phonecode_id: '',
          document: '',
          document_type_id: '',
          state_id: '',
          city_id: '',
          gender_id: '',
        };

    try {
      const res: any = await ProfileService.update({
        user_id: this.props.user.id,
        ...this.state.form,
        ...extraFields,
        hasFile: true,
        level_id: this.props.user?.level_id,
        practice_since:
          this.state.form.practice_since &&
          moment(this.state.form.practice_since).format('YYYY-MM-DD'),
        user_areas: JSON.stringify(this.state.user_areas),
        withoutError: true,
      });
      Globals.showSuccess('Se ha actualizado el perfil correctamente');
      this.props.dispatch({
        type: 'SET_USER',
        payload: res.user,
      });
      this.setState({
        form: {
          ...this.state.form,
          password: '',
          password_confirmation: '',
        },
      });
    } catch (err: any) {
      const data = err?.response?.data;
      // El interceptor global del API envuelve el error en data.error
      const message =
        data?.error?.message ||
        (typeof data?.message === 'string' ? data.message : null) ||
        'Error al actualizar el perfil';
      Globals.showError(message);
    }
  };

  change = (e: any, callback = () => {}) => {
    this.setState(
      {
        form: {
          ...this.state.form,
          [e.target.name]: e.target.value,
        },
      },
      callback,
    );
  };

  changeCountry = (e: any) => {
    this.setState({
      form: {
        ...this.state.form,
        country: e.target.value,
        state_id: '',
        city_id: '',
      },
      cities: [],
    });
  };

  changeState = (e: any) => {
    this.setState(
      {
        form: {
          ...this.state.form,
          state_id: e.target.value,
          city_id: '',
        },
      },
      () => {
        this.getCities();
      },
    );
  };

  deleteArea = (e: any, index: number) => {
    e.preventDefault();
    const user_areas: any = [...this.state.user_areas];
    user_areas.splice(index, 1);
    this.setState({ user_areas });
  };

  getCities = async () => {
    const res: any = await AuthService.getCities({
      state_id: this.state.form.state_id,
    });
    this.setState({
      cities: res.cities || [],
    });
  };

  add = (membership_id: number | null = null) => {
    this.props.history.push(
      `/${this.props.user?.level_id == Constants.LEVELS.CLIENT ? 'client' : 'lawyer'}/memberships/add${membership_id ? '?membership_id=' + membership_id : ''}`,
    );
  };

  render() {
    const isAdmin =
      this.props.user?.level_id == Constants.LEVELS.ADMIN ||
      this.props.user?.level_id == Constants.LEVELS.MODERATOR;

    const showMembershipCard =
      !isAdmin &&
      this.props.user?.internal !== Constants.LAWYER_INTERNAL.YES &&
      (this.props.user?.level_id !== Constants.LEVELS.LAWYER ||
        this.props.user?.verified === Constants.VERIFIED.VERIFIED);

    return (
      <div id='profile'>
        <form className='row' onSubmit={this.submit}>
          <div className='col-md-12'>
            <div className='container-form row'>
              <div className='col-md-12'>
                <h3>Datos Personales</h3>
              </div>
              <div className='col-md-12'>
                <div className='row'>
                  <div className='col-md-6'>
                    <Input
                      color='white'
                      value={this.state.form.name}
                      name='name'
                      label='Nombre'
                      onChange={this.change}
                    />
                  </div>
                  <div className='col-md-6'>
                    <Input
                      color='white'
                      value={this.state.form.lastname}
                      name='lastname'
                      label='Apellido'
                      onChange={this.change}
                    />
                  </div>
                  <div className='col-md-6'>
                    <Input
                      color='white'
                      value={this.state.form.email}
                      name='email'
                      label='Correo Electrónico'
                      onChange={this.change}
                    />
                  </div>
                  {(this.props.user?.level_id == Constants.LEVELS.CLIENT ||
                    this.props.user?.level_id == Constants.LEVELS.LAWYER) && (
                    <>
                      <div className='col-md-6'>
                        <label className='label-select'>Cédula o RIF</label>
                        <div className='container-select'>
                          <Select
                            color='white'
                            placeholder='Seleccionar'
                            name='document_type_id'
                            onChange={this.change}
                            value={this.state.form.document_type_id}
                            options={this.state.document_types.map((item: any) => {
                              return {
                                value: item.id,
                                label: item.name,
                              };
                            })}
                          />
                          <Input
                            type='number'
                            color='white'
                            value={this.state.form.document}
                            name='document'
                            maxLength={9}
                            onChange={this.change}
                          />
                        </div>
                      </div>
                      <div className='col-md-6'>
                        <label className='label-select'>Teléfono</label>
                        <div className='container-select'>
                          <Select
                            color='white'
                            placeholder='Código'
                            name='phone_code'
                            style={{ width: 115 }}
                            onChange={this.change}
                            value={this.state.form.phone_code}
                            options={PHONE_CODES}
                          />
                          <Input
                            type='number'
                            color='white'
                            maxLength={15}
                            value={this.state.form.phone}
                            name='phone'
                            onChange={this.change}
                          />
                        </div>
                      </div>

                      {(() => {
                        const isVenezuela = this.state.form.country === 'VE';
                        return (
                          <>
                            <div className='col-md-6'>
                              <Select
                                color='white'
                                placeholder='País'
                                label='País'
                                name='country'
                                onChange={this.changeCountry}
                                value={this.state.form.country}
                                options={COUNTRIES}
                              />
                            </div>
                            <div className='col-md-6'>
                              <Select
                                disabled={!isVenezuela} // Bloquear si no es VE
                                color='white'
                                label='Estado'
                                name='state_id'
                                className={!isVenezuela ? 'select-disabled' : ''}
                                onChange={this.changeState}
                                value={this.state.form.state_id}
                                options={this.state.states.map((item: any) => ({
                                  value: item.id,
                                  label: item.name,
                                }))}
                              />
                            </div>
                            <div className='col-md-6'>
                              <Select
                                disabled={!isVenezuela || this.state.form.state_id === ''} // Bloquear si no es VE o no hay estado
                                color='white'
                                label='Ciudad'
                                name='city_id'
                                className={
                                  !isVenezuela || this.state.form.state_id === ''
                                    ? 'select-disabled'
                                    : ''
                                }
                                onChange={this.change}
                                value={this.state.form.city_id}
                                options={this.state.cities.map((item: any) => ({
                                  value: item.id,
                                  label: item.name,
                                }))}
                              />
                            </div>
                          </>
                        );
                      })()}

                      <div className='col-md-6'>
                        <Select
                          color='white'
                          label='Genero'
                          name='gender_id'
                          onChange={this.change}
                          value={this.state.form.gender_id}
                          options={this.state.genders.map((item: any) => {
                            return {
                              value: item.id,
                              label: item.name,
                            };
                          })}
                        />
                      </div>
                      {this.props.user?.level_id == Constants.LEVELS.LAWYER && (
                        <>
                          <div className='col-md-6'>
                            <DatePicker
                              label='Año de comienzo de ejercicio'
                              placeholder='Año en el que comenzó a ejercer'
                              value={this.state.form.practice_since}
                              maxDate={moment().toDate()}
                              showMonthYearPicker
                              dateFormat='MM/yyyy'
                              onChange={(text: string) => {
                                this.change({
                                  target: {
                                    value: text,
                                    name: 'practice_since',
                                  },
                                });
                              }}
                            />
                          </div>
                          <div className='col-md-12'>
                            <label>Biografía</label>
                            <textarea
                              className='form-control'
                              rows={1}
                              value={this.state.form.biography}
                              placeholder='Escribir'
                              name='biography'
                              style={{
                                height: 100,
                                marginTop: 5,
                                resize: 'none',
                              }}
                              onChange={this.change}
                            />
                          </div>
                          <div className='col-md-12 mt-3'>
                            <AreaSelector
                              areas={this.state.areas}
                              userAreas={this.state.user_areas}
                              labelSelect='Agregar tipo de derecho'
                              splitLayout={true}
                              onChange={(updated) => this.setState({ user_areas: updated })}
                            />
                          </div>
                        </>
                      )}
                    </>
                  )}
                  {this.props.user?.level_id == Constants.LEVELS.LAWYER && (
                    <>
                      <div className='col-md-12 mt-5'>
                        <h3>Datos Bancarios (Opcional)</h3>
                      </div>
                      <div className='col-md-12 text-right'>
                        <h6>Transferencia</h6>
                      </div>
                      <div className='col-md-6'>
                        <Select
                          color='white'
                          label='Banco'
                          name='transfer_bank_id'
                          onChange={(e: any) => this.change(e)}
                          value={this.state.form.transfer_bank_id}
                          options={this.state.banks.map((item: any) => {
                            return {
                              value: item.id,
                              label: item.name,
                            };
                          })}
                        />
                      </div>
                      <div className='col-md-6'>
                        <Input
                          type='number'
                          color='white'
                          value={this.state.form.transfer_account}
                          name='transfer_account'
                          label='Número de Cuenta'
                          onChange={this.change}
                        />
                      </div>
                      <div className='col-md-6'>
                        <Select
                          color='white'
                          label='Tipo de cuenta'
                          name='transfer_type'
                          onChange={(e: any) => this.change(e)}
                          value={this.state.form.transfer_type}
                          options={[
                            {
                              value: Constants.ACCOUNT_TYPES.SAVING.toString(),
                              label: 'Cuenta de Ahorros',
                            },
                            {
                              value: Constants.ACCOUNT_TYPES.CHECKING.toString(),
                              label: 'Cuenta Corriente',
                            },
                          ]}
                        />
                      </div>
                      <div className='col-md-6'>
                        <label className='label-select'>Cédula o RIF</label>
                        <div className='container-select'>
                          <Select
                            color='white'
                            placeholder='Seleccionar'
                            name='transfer_document_type_id'
                            onChange={this.change}
                            value={this.state.form.transfer_document_type_id}
                            options={this.state.document_types.map((item: any) => {
                              return {
                                value: item.id,
                                label: item.name,
                              };
                            })}
                          />
                          <Input
                            type='number'
                            color='white'
                            value={this.state.form.transfer_document}
                            name='transfer_document'
                            maxLength={9}
                            onChange={this.change}
                          />
                        </div>
                      </div>
                    </>
                  )}
                  {(this.props.user?.level_id == Constants.LEVELS.CLIENT ||
                    this.props.user?.level_id == Constants.LEVELS.LAWYER) && (
                    <>
                      {this.props.user?.level_id !== Constants.LEVELS.LAWYER && (
                        <div className='col-md-12 mt-5'>
                          <h3>Datos Bancarios (Opcional)</h3>
                        </div>
                      )}
                      <div className='col-md-12 text-right'>
                        <h6>Pago Móvil</h6>
                      </div>
                      <div className='col-md-6'>
                        <Select
                          color='white'
                          label='Banco'
                          name='payment_mobile_bank_id'
                          onChange={(e: any) => this.change(e)}
                          value={this.state.form.payment_mobile_bank_id}
                          options={this.state.banks.map((item: any) => {
                            return {
                              value: item.id,
                              label: item.name,
                            };
                          })}
                        />
                      </div>
                      <div className='col-md-6'>
                        <label className='label-select'>Teléfono</label>
                        <div className='container-select'>
                          <Select
                            color='white'
                            placeholder='Seleccionar'
                            name='payment_mobile_phonecode_id'
                            onChange={this.change}
                            value={this.state.form.payment_mobile_phonecode_id}
                            options={this.state.phonecodes.map((item: any) => {
                              return {
                                value: item.id,
                                label: item.name,
                              };
                            })}
                          />
                          <Input
                            type='number'
                            color='white'
                            maxLength={7}
                            value={this.state.form.payment_mobile_phone}
                            name='payment_mobile_phone'
                            onChange={this.change}
                          />
                        </div>
                      </div>
                      <div className='col-md-6'>
                        <label className='label-select'>Cédula o RIF</label>
                        <div className='container-select'>
                          <Select
                            color='white'
                            placeholder='Seleccionar'
                            name='payment_mobile_document_type_id'
                            onChange={this.change}
                            value={this.state.form.payment_mobile_document_type_id}
                            options={this.state.document_types.map((item: any) => {
                              return {
                                value: item.id,
                                label: item.name,
                              };
                            })}
                          />
                          <Input
                            type='number'
                            color='white'
                            value={this.state.form.payment_mobile_document}
                            name='payment_mobile_document'
                            maxLength={9}
                            onChange={this.change}
                          />
                        </div>
                      </div>
                    </>
                  )}
                  <div className='col-md-12'>
                    <h3>Datos de Acceso</h3>
                  </div>
                  <div className='col-md-6'>
                    <label>Cambiar contraseña (Opcional)</label>
                    <div className='input-password-wrapper'>
                      <Input
                        type={this.state.activePassword ? 'text' : 'password'}
                        color='white'
                        value={this.state.form.password}
                        name='password'
                        placeholder='Contraseña'
                        onChange={this.change}
                      />
                      <div
                        className='password-view'
                        onClick={() =>
                          this.setState({
                            activePassword: !this.state.activePassword,
                          })
                        }
                      >
                        <img src={this.state.activePassword ? PasswordActive : PasswordInactive} />
                      </div>
                    </div>
                  </div>
                  <div className='col-md-6'>
                    <label>Repetir contraseña</label>
                    <div className='input-password-wrapper'>
                      <Input
                        type={this.state.activePasswordConfirmation ? 'text' : 'password'}
                        color='white'
                        value={this.state.form.password_confirmation}
                        name='password_confirmation'
                        placeholder='Contraseña'
                        onChange={this.change}
                      />
                      <div
                        className='password-view'
                        onClick={() =>
                          this.setState({
                            activePasswordConfirmation: !this.state.activePasswordConfirmation,
                          })
                        }
                      >
                        <img
                          src={
                            this.state.activePasswordConfirmation
                              ? PasswordActive
                              : PasswordInactive
                          }
                        />
                      </div>
                    </div>
                  </div>
                  <div className='col-12 text-center'>
                    <Button type='submit'>Guardar</Button>
                  </div>
                </div>
              </div>
              {/* Membresía: visible para clientes y abogados verificados */}
              {/* {showMembershipCard && (
                <div className='col-md-5'>
                  <ContainerCardMembership plan_user={this.state.plan_user} load={this.load} />
                </div>
              )} */}
            </div>
          </div>
        </form>
      </div>
    );
  }
}

export default connect((state: RootState) => {
  return {
    user: state.user,
  };
})(Profile);
